import type { InterfaceNamespaceTranslationType } from '@unifyapps/defs/types/namespace';
import type { TFunction } from 'i18next';
import { produce } from 'immer';
import { has, set } from 'lodash';

const keys = [
  'title',
  'placeholder',
  'searchPlaceholder',
  'label',
  'description',
  'hint',
  'help',
  'displayName',
  'addButtonTitle',
  'firstStepPlaceholder',
  'otherStepPlaceholder',
  'mappedFilterRefHelpText',
  'field_label',
  'name',
  'pluralName',
  'disabledTooltip',
].flatMap((v) => [`ui:${v}`, `ua:${v}`, v]);

function objectVisitor(
  object: Record<string, unknown>,
  visit: (object: Record<string, unknown>, key: string) => void,
) {
  for (const key in object) {
    if (typeof object[key] === 'object') {
      objectVisitor(object[key] as Record<string, unknown>, visit);
    } else {
      visit(object, key);
    }
  }
}

/**
 * Example of translations object:
 * ```typescript
 * const translations = {
 *   "table_block['component']['columns']['1']['meta']['labelMapping']['ai-agent']": {
 *     value: "Agent"
 *   }
 * }
 * ```
 *
 * The keys in translations object represent the path to the property in the object that needs to be updated.
 * This value will be used instead of what's returned by the "t" function.
 */
export function getTranslatedObject<T = unknown>(
  object: T,
  t: TFunction,
  translations?: Record<string, InterfaceNamespaceTranslationType | undefined>,
): T {
  return produce(object, (draft) => {
    objectVisitor(draft as Record<string, unknown>, (obj, key) => {
      const value = obj[key];
      if (typeof key === 'string' && typeof value === 'string' && keys.includes(key)) {
        // disable nsSeparator so values containing ":" (e.g. "wss://...") aren't parsed as namespace:key by i18next
        obj[key] = t(value, { nsSeparator: false, defaultValue: value });
      }
    });

    if (translations) {
      for (const key in translations) {
        if (has(draft, key) && translations[key]) {
          set(draft, key, translations[key].value);
        }
      }
    }
  });
}
